import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { usePermissions } from '../context/PermissionsContext';
import { getSettings, saveSettings, getDepartments, saveDepartments } from '../api';
import api from '../api';

const DELETE_REASONS = [
  'Too expensive for what I need',
  "Missing a feature I need",
  'Switching to a different tool',
  "Just not using it enough",
  'Other',
];

export default function SettingsPage() {
  const { role } = usePermissions();
  const [isPlatformAdmin, setIsPlatformAdmin] = React.useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = React.useState(false);
  const [orphanedTasks,  setOrphanedTasks]  = React.useState([]);
  const [cleaningOrphans, setCleaningOrphans] = React.useState(false);
  const [cleanupMsg,     setCleanupMsg]     = React.useState('');
  const [deleteStep,     setDeleteStep]     = React.useState(1);
  const [deleteReason,   setDeleteReason]   = React.useState('');
  const [deleteNote,     setDeleteNote]     = React.useState('');
  const [confirmText,    setConfirmText]    = React.useState('');
  const [deleteError,    setDeleteError]    = React.useState('');
  const [deletingAccount,setDeletingAccount]= React.useState(false);
  const currentPlan = (() => {
    try {
      const stored = JSON.parse(localStorage.getItem('gpf_user') || '{}');
      if (stored?.role === 'administrator') return 'business'; // platform superadmin — treat as top tier, no upsell needed
      const workspace = JSON.parse(localStorage.getItem('gpf_workspace_cache') || '{}');
      return workspace?.plan || 'free';
    } catch { return 'free'; }
  })();
  const isFreePlan = currentPlan === 'free';

  // Check platform admin status directly from server (reliable regardless of localStorage)
  React.useEffect(() => {
    api.get('/wp/v2/users/me?context=edit')
      .then(res => {
        const roles = res.data.roles || [];
        setIsPlatformAdmin(roles.includes('administrator'));
      })
      .catch(() => setIsPlatformAdmin(false));
  }, []);
  const [apiKey,       setApiKey]       = useState('');
  const [hasKey,       setHasKey]       = useState(false);
  const [keyPreview,   setKeyPreview]   = useState('');
  const [placesKey,       setPlacesKey]       = useState('');
  const [hasPlacesKey,    setHasPlacesKey]    = useState(false);
  const [placesKeyPreview,setPlacesKeyPreview]= useState('');
  const [savingPlacesKey, setSavingPlacesKey] = useState(false);
  const [placesKeyMsg,    setPlacesKeyMsg]    = useState('');
  const [stripeSecretKey,      setStripeSecretKey]      = useState('');
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [stripePriceTeam,      setStripePriceTeam]      = useState('');
  const [stripePriceBusiness,  setStripePriceBusiness]  = useState('');
  const [stripeWebhookSecret,  setStripeWebhookSecret]  = useState('');
  const [hasStripeKey,         setHasStripeKey]         = useState(false);
  const [stripeKeyPreview,     setStripeKeyPreview]     = useState('');
  const [hasWebhookSecret,     setHasWebhookSecret]     = useState(false);
  const [webhookUrl,           setWebhookUrl]           = useState('');
  const [savingStripe,         setSavingStripe]         = useState(false);
  const [stripeMsg,            setStripeMsg]            = useState('');
  const [departments,  setDepartments]  = useState([]);
  const [newDept,      setNewDept]      = useState('');
  const [savingKey,    setSavingKey]    = useState(false);
  const [savingDepts,  setSavingDepts]  = useState(false);
  const [keyMsg,       setKeyMsg]       = useState('');
  const [deptMsg,      setDeptMsg]      = useState('');
  const [timezone,     setTimezone]     = useState('');
  const [tzMsg,        setTzMsg]        = useState('');
  const [savingTz,     setSavingTz]     = useState(false);

  useEffect(() => {
    api.get('/wp/v2/users/me?context=edit')
      .then(res => setTimezone(res.data.gpf_timezone || 'America/New_York'))
      .catch(console.error);

    getSettings().then(res => {
      setHasKey(res.data.has_api_key);
      setKeyPreview(res.data.api_key_preview);
      setHasPlacesKey(res.data.has_places_api_key);
      setPlacesKeyPreview(res.data.places_api_key_preview);
    }).catch(console.error);

    api.get('/gpf/v1/billing/settings').then(res => {
      setHasStripeKey(res.data.has_secret_key);
      setStripeKeyPreview(res.data.secret_preview);
      setStripePublishableKey(res.data.publishable_key || '');
      setStripePriceTeam(res.data.price_team || '');
      setStripePriceBusiness(res.data.price_business || '');
      setHasWebhookSecret(res.data.has_webhook_secret);
      setWebhookUrl(res.data.webhook_url || `${window.location.origin.replace('app.', '')}/wp-json/gpf/v1/billing/webhook`);
    }).catch(() => {
      // Fallback if the endpoint isn't reachable yet (e.g. permalinks not flushed) —
      // still show the correct URL so setup isn't blocked.
      setWebhookUrl('https://goprojectflow.com/wp-json/gpf/v1/billing/webhook');
    });

    getDepartments().then(res => {
      setDepartments(res.data);
    }).catch(console.error);

    api.get('/gpf/v1/orphaned-tasks').then(res => {
      setOrphanedTasks(res.data || []);
    }).catch(console.error);
  }, []);

  const handleCleanupOrphans = async () => {
    if (!window.confirm(`Permanently delete ${orphanedTasks.length} orphaned task(s)? This cannot be undone.`)) return;
    setCleaningOrphans(true);
    setCleanupMsg('');
    try {
      const res = await api.post('/gpf/v1/orphaned-tasks/cleanup');
      setOrphanedTasks([]);
      setCleanupMsg(`✅ Deleted ${res.data.deleted} orphaned task${res.data.deleted !== 1 ? 's' : ''}.`);
    } catch (e) {
      setCleanupMsg('Failed to clean up orphaned tasks.');
    } finally {
      setCleaningOrphans(false);
    }
  };

  const handleSaveKey = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setSavingKey(true);
    try {
      await saveSettings({ anthropic_api_key: apiKey });
      setHasKey(true);
      setKeyPreview(apiKey.slice(0, 8) + '...');
      setApiKey('');
      setKeyMsg('API key saved successfully!');
      setTimeout(() => setKeyMsg(''), 3000);
    } catch (err) {
      setKeyMsg('Failed to save API key.');
    } finally {
      setSavingKey(false);
    }
  };

  const handleSavePlacesKey = async (e) => {
    e.preventDefault();
    if (!placesKey.trim()) return;
    setSavingPlacesKey(true);
    try {
      await saveSettings({ google_places_api_key: placesKey });
      setHasPlacesKey(true);
      setPlacesKeyPreview(placesKey.slice(0, 8) + '...');
      setPlacesKey('');
      setPlacesKeyMsg('API key saved successfully!');
      setTimeout(() => setPlacesKeyMsg(''), 3000);
    } catch (err) {
      setPlacesKeyMsg('Failed to save API key.');
    } finally {
      setSavingPlacesKey(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') return;
    setDeletingAccount(true);
    setDeleteError('');
    try {
      await api.post('/gpf/v1/account/delete-my-account', { reason: deleteReason, note: deleteNote });
      localStorage.clear();
      window.location.href = 'https://goprojectflow.com';
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to delete account. Please try again or contact hi@goprojectflow.com.');
      setDeletingAccount(false);
    }
  };

  const handleSaveStripeSettings = async (e) => {
    e.preventDefault();
    setSavingStripe(true);
    setStripeMsg('');
    try {
      await api.post('/gpf/v1/billing/settings', {
        stripe_secret_key:      stripeSecretKey || undefined,
        stripe_publishable_key: stripePublishableKey || undefined,
        stripe_price_team:      stripePriceTeam || undefined,
        stripe_price_business:  stripePriceBusiness || undefined,
        stripe_webhook_secret:  stripeWebhookSecret || undefined,
      });
      if (stripeSecretKey) { setHasStripeKey(true); setStripeKeyPreview(stripeSecretKey.slice(0, 11) + '...'); setStripeSecretKey(''); }
      if (stripeWebhookSecret) { setHasWebhookSecret(true); setStripeWebhookSecret(''); }
      setStripeMsg('Stripe settings saved successfully!');
      setTimeout(() => setStripeMsg(''), 3000);
    } catch (err) {
      setStripeMsg('Failed to save Stripe settings.');
    } finally {
      setSavingStripe(false);
    }
  };

  const handleAddDept = () => {
    const trimmed = newDept.trim();
    if (!trimmed || departments.includes(trimmed)) return;
    setDepartments([...departments, trimmed]);
    setNewDept('');
  };

  const handleRemoveDept = (dept) => {
    setDepartments(departments.filter(d => d !== dept));
  };

  const handleSaveTz = async () => {
    setSavingTz(true);
    try {
      await api.post('/gpf/v1/profile', { gpf_timezone: timezone });
      setTzMsg('Timezone saved!');
      setTimeout(() => setTzMsg(''), 3000);
    } catch (err) { setTzMsg('Failed to save timezone.'); }
    finally { setSavingTz(false); }
  };

  const handleSaveDepts = async () => {
    setSavingDepts(true);
    try {
      await saveDepartments(departments);
      setDeptMsg('Departments saved!');
      setTimeout(() => setDeptMsg(''), 3000);
    } catch (err) {
      setDeptMsg(err.response?.data?.message || 'Failed to save departments.');
    } finally {
      setSavingDepts(false);
    }
  };

  const DEPT_COLORS = ['#2563eb','#7c3aed','#0891b2','#16a34a','#d97706','#dc2626','#db2777','#65a30d','#0d9488','#9333ea'];

  return (
    <Layout>
      <div style={styles.page}>

        <div style={styles.header}>
          <h1 style={styles.title}>Settings</h1>
          <p style={styles.subtitle}>Configure AI features and departments</p>
        </div>

        {currentPlan !== 'business' && (
          <div style={styles.settingsUpgradeBanner}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 2px' }}>
                {currentPlan === 'free' ? "You're on the Free plan" : "You're on the Team plan"}
              </p>
              <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                {currentPlan === 'free'
                  ? 'Upgrade to Team or Business to unlock unlimited projects, users, and more.'
                  : 'Upgrade to Business to unlock the AI Agent, Research Center, and custom permissions.'}
              </p>
            </div>
            <a href="/workspace?upgrade=1" style={styles.settingsUpgradeBtn}>Upgrade Plan →</a>
          </div>
        )}

        {/* ── AI Configuration - account owners only ── */}
        {isPlatformAdmin && <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>🤖 AI Configuration</h2>
              <p style={styles.cardDesc}>Connect your Anthropic API key to enable AI-powered department classification.</p>
            </div>
            {hasKey && (
              <div style={styles.connectedBadge}>
                <span style={styles.connectedDot} />
                Connected
              </div>
            )}
          </div>

          {hasKey && (
            <div style={styles.currentKey}>
              <span style={styles.currentKeyLabel}>Current key:</span>
              <code style={styles.currentKeyValue}>{keyPreview}</code>
            </div>
          )}

          <form onSubmit={handleSaveKey} style={styles.keyForm}>
            <input
              style={styles.input}
              type="password"
              placeholder={hasKey ? 'Enter new key to replace existing...' : 'sk-ant-...'}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
            />
            <button type="submit" style={styles.primaryBtn} disabled={savingKey}>
              {savingKey ? 'Saving...' : hasKey ? 'Update Key' : 'Save Key'}
            </button>
          </form>
          {keyMsg && <p style={{ ...styles.msg, color: keyMsg.includes('success') ? '#16a34a' : '#dc2626' }}>{keyMsg}</p>}

          <div style={styles.infoBox}>
            <p style={styles.infoText}>
              Get your API key at <a href="https://console.anthropic.com" target="_blank" rel="noreferrer" style={styles.link}>console.anthropic.com</a>.
              The key is stored securely on your server and never exposed to the browser.
            </p>
          </div>
        </div>}

        {/* ── Google Places API - powers real business counts in Research Center ── */}
        {isPlatformAdmin && <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>📍 Google Places API</h2>
              <p style={styles.cardDesc}>Optional — enables real, accurate business counts in the Research Center's city-level results, instead of AI-estimated figures.</p>
            </div>
            {hasPlacesKey && (
              <div style={styles.connectedBadge}>
                <span style={styles.connectedDot} />
                Connected
              </div>
            )}
          </div>

          {hasPlacesKey && (
            <div style={styles.currentKey}>
              <span style={styles.currentKeyLabel}>Current key:</span>
              <code style={styles.currentKeyValue}>{placesKeyPreview}</code>
            </div>
          )}

          <form onSubmit={handleSavePlacesKey} style={styles.keyForm}>
            <input
              style={styles.input}
              type="password"
              placeholder={hasPlacesKey ? 'Enter new key to replace existing...' : 'AIza...'}
              value={placesKey}
              onChange={e => setPlacesKey(e.target.value)}
            />
            <button type="submit" style={styles.primaryBtn} disabled={savingPlacesKey}>
              {savingPlacesKey ? 'Saving...' : hasPlacesKey ? 'Update Key' : 'Save Key'}
            </button>
          </form>
          {placesKeyMsg && <p style={{ ...styles.msg, color: placesKeyMsg.includes('success') ? '#16a34a' : '#dc2626' }}>{placesKeyMsg}</p>}

          <div style={styles.infoBox}>
            <p style={styles.infoText}>
              Get a key at <a href="https://console.cloud.google.com/google/maps-apis" target="_blank" rel="noreferrer" style={styles.link}>console.cloud.google.com</a> with the "Places API" enabled.
              Without this key, city-level counts fall back to AI-researched estimates.
            </p>
          </div>
        </div>}

        {/* ── Stripe Billing ── */}
        {isPlatformAdmin && <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>💳 Stripe Billing</h2>
              <p style={styles.cardDesc}>Connect Stripe to accept real subscription payments for Team and Business plans.</p>
            </div>
            {hasStripeKey && (
              <div style={styles.connectedBadge}>
                <span style={styles.connectedDot} />
                Connected
              </div>
            )}
          </div>

          <div style={styles.webhookBox}>
            <label style={styles.smallLabel}>Webhook URL — add this in Stripe → Developers → Webhooks</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                readOnly
                value={webhookUrl || 'Loading...'}
                style={{ ...styles.input, fontFamily: 'monospace', fontSize: 12, background: '#f8fafc', flex: 1 }}
                onClick={e => e.target.select()}
              />
              <button
                type="button"
                style={styles.copyBtn}
                onClick={() => { navigator.clipboard.writeText(webhookUrl); setStripeMsg('Webhook URL copied!'); setTimeout(() => setStripeMsg(''), 2000); }}
              >
                📋 Copy
              </button>
            </div>
          </div>

          {hasStripeKey && (
            <div style={styles.currentKey}>
              <span style={styles.currentKeyLabel}>Current secret key:</span>
              <code style={styles.currentKeyValue}>{stripeKeyPreview}</code>
            </div>
          )}

          <form onSubmit={handleSaveStripeSettings} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={styles.smallLabel}>Secret Key</label>
              <input
                style={styles.input}
                type="password"
                placeholder={hasStripeKey ? 'Enter new key to replace existing...' : 'sk_live_... or sk_test_...'}
                value={stripeSecretKey}
                onChange={e => setStripeSecretKey(e.target.value)}
              />
            </div>
            <div>
              <label style={styles.smallLabel}>Publishable Key</label>
              <input
                style={styles.input}
                placeholder="pk_live_... or pk_test_..."
                value={stripePublishableKey}
                onChange={e => setStripePublishableKey(e.target.value)}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={styles.smallLabel}>Team Plan — Price ID</label>
                <input
                  style={styles.input}
                  placeholder="price_..."
                  value={stripePriceTeam}
                  onChange={e => setStripePriceTeam(e.target.value)}
                />
                {stripePriceTeam && !stripePriceTeam.startsWith('price_') && (
                  <p style={styles.fieldWarning}>
                    ⚠️ {stripePriceTeam.startsWith('prod_')
                      ? "This looks like a Product ID. Open this product in Stripe, go to Pricing, and copy the ID starting with 'price_' instead."
                      : "Price IDs should start with 'price_'."}
                  </p>
                )}
              </div>
              <div>
                <label style={styles.smallLabel}>Business Plan — Price ID</label>
                <input
                  style={styles.input}
                  placeholder="price_..."
                  value={stripePriceBusiness}
                  onChange={e => setStripePriceBusiness(e.target.value)}
                />
                {stripePriceBusiness && !stripePriceBusiness.startsWith('price_') && (
                  <p style={styles.fieldWarning}>
                    ⚠️ {stripePriceBusiness.startsWith('prod_')
                      ? "This looks like a Product ID. Open this product in Stripe, go to Pricing, and copy the ID starting with 'price_' instead."
                      : "Price IDs should start with 'price_'."}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label style={styles.smallLabel}>Webhook Signing Secret {hasWebhookSecret && '(configured)'}</label>
              <input
                style={styles.input}
                type="password"
                placeholder={hasWebhookSecret ? 'Enter new secret to replace existing...' : 'whsec_...'}
                value={stripeWebhookSecret}
                onChange={e => setStripeWebhookSecret(e.target.value)}
              />
            </div>
            <button type="submit" style={{ ...styles.primaryBtn, alignSelf: 'flex-start' }} disabled={savingStripe}>
              {savingStripe ? 'Saving...' : 'Save Stripe Settings'}
            </button>
          </form>
          {stripeMsg && <p style={{ ...styles.msg, color: stripeMsg.includes('success') ? '#16a34a' : '#dc2626' }}>{stripeMsg}</p>}

          <div style={styles.infoBox}>
            <p style={styles.infoText}>
              1. Create two Products in your <a href="https://dashboard.stripe.com/products" target="_blank" rel="noreferrer" style={styles.link}>Stripe Dashboard</a> — "Team" at $7/user/month and "Business" at $17/user/month — each set to <strong>per-unit, recurring monthly</strong> pricing, and paste their Price IDs above.<br/>
              2. Copy the Webhook URL above into Stripe → Developers → Webhooks → Add endpoint.<br/>
              3. Subscribe it to: <code>checkout.session.completed</code>, <code>customer.subscription.updated</code>, <code>customer.subscription.deleted</code>, <code>invoice.payment_failed</code> — then paste the signing secret above.
            </p>
          </div>
        </div>}

        {/* ── Timezone Setting ── */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2 style={styles.cardTitle}>🌍 Account Timezone</h2>
              <p style={styles.cardDesc}>Dates and times across your account will reflect this timezone.</p>
            </div>
            {timezone && (
              <span style={{ fontSize: 12, color: '#2563eb', background: '#eff6ff', padding: '4px 12px', borderRadius: 99, fontWeight: 600, alignSelf: 'center' }}>
                {timezone.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginTop: 16 }}>
            {[
              { label: '🌎 Americas', zones: [
                { value: 'America/New_York',    label: 'Eastern Time (ET)',      offset: 'UTC-5' },
                { value: 'America/Chicago',     label: 'Central Time (CT)',      offset: 'UTC-6' },
                { value: 'America/Denver',      label: 'Mountain Time (MT)',     offset: 'UTC-7' },
                { value: 'America/Los_Angeles', label: 'Pacific Time (PT)',      offset: 'UTC-8' },
                { value: 'America/Toronto',     label: 'Toronto',                offset: 'UTC-5' },
                { value: 'America/Vancouver',   label: 'Vancouver',              offset: 'UTC-8' },
                { value: 'America/Sao_Paulo',   label: 'São Paulo',              offset: 'UTC-3' },
                { value: 'America/Mexico_City', label: 'Mexico City',            offset: 'UTC-6' },
              ]},
              { label: '🌍 Europe & Africa', zones: [
                { value: 'Europe/London',       label: 'London (GMT)',           offset: 'UTC+0' },
                { value: 'Europe/Paris',        label: 'Paris / Berlin',         offset: 'UTC+1' },
                { value: 'Europe/Madrid',       label: 'Madrid / Rome',          offset: 'UTC+1' },
                { value: 'Europe/Amsterdam',    label: 'Amsterdam',              offset: 'UTC+1' },
                { value: 'Africa/Lagos',        label: 'Lagos / Abuja',          offset: 'UTC+1' },
                { value: 'Africa/Nairobi',      label: 'Nairobi',                offset: 'UTC+3' },
                { value: 'Africa/Johannesburg', label: 'Johannesburg',           offset: 'UTC+2' },
              ]},
              { label: '🌏 Asia & Pacific', zones: [
                { value: 'Asia/Dubai',          label: 'Dubai',                  offset: 'UTC+4' },
                { value: 'Asia/Kolkata',        label: 'India (IST)',            offset: 'UTC+5:30' },
                { value: 'Asia/Singapore',      label: 'Singapore',              offset: 'UTC+8' },
                { value: 'Asia/Tokyo',          label: 'Tokyo',                  offset: 'UTC+9' },
                { value: 'Asia/Shanghai',       label: 'Shanghai / Beijing',     offset: 'UTC+8' },
                { value: 'Asia/Seoul',          label: 'Seoul',                  offset: 'UTC+9' },
                { value: 'Australia/Sydney',    label: 'Sydney',                 offset: 'UTC+11' },
                { value: 'Pacific/Auckland',    label: 'Auckland',               offset: 'UTC+13' },
                { value: 'Pacific/Honolulu',    label: 'Honolulu (HST)',         offset: 'UTC-10' },
              ]},
            ].map(group => (
              <div key={group.label} style={{ background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{group.label}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {group.zones.map(tz => (
                    <button
                      key={tz.value}
                      onClick={() => setTimezone(tz.value)}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '7px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                        background: timezone === tz.value ? '#2563eb' : 'white',
                        color: timezone === tz.value ? 'white' : '#374151',
                        fontSize: 13, fontWeight: timezone === tz.value ? 600 : 400,
                        transition: 'all 0.15s', textAlign: 'left',
                      }}
                    >
                      <span>{tz.label}</span>
                      <span style={{ fontSize: 11, opacity: 0.7, fontFamily: 'monospace' }}>{tz.offset}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              style={{ padding: '10px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              onClick={handleSaveTz}
              disabled={savingTz}
            >
              {savingTz ? 'Saving...' : '💾 Save Timezone'}
            </button>
            {tzMsg && (
              <span style={{ fontSize: 13, color: tzMsg.includes('saved') ? '#16a34a' : '#dc2626', fontWeight: 500 }}>
                {tzMsg}
              </span>
            )}
          </div>
        </div>

        {/* ── Department Management ── */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>🏢 AI-Powered Departments</h2>
          <p style={styles.cardDesc}>
            When a project is created, AI automatically classifies it into one of these departments.
            Add, remove, or rename departments to match your organization.
          </p>

          <div style={styles.deptGrid}>
            {departments.map((dept, i) => (
              <div key={dept} style={{ ...styles.deptChip, borderLeft: `3px solid ${DEPT_COLORS[i % DEPT_COLORS.length]}` }}>
                <span style={styles.deptName}>{dept}</span>
                {!isFreePlan && (
                  <button
                    style={styles.deptRemove}
                    onClick={() => handleRemoveDept(dept)}
                    title="Remove department"
                  >✕</button>
                )}
              </div>
            ))}
          </div>

          {isFreePlan ? (
            <div style={styles.upgradeNote}>
              <span>🔒 Customizing departments is available on the Team and Business plans.</span>
              <a href="/workspace?upgrade=1" style={styles.upgradeNoteLink}>Upgrade →</a>
            </div>
          ) : (
            <div style={styles.addDeptRow}>
              <input
                style={{ ...styles.input, flex: 1 }}
                placeholder="Add new department..."
                value={newDept}
                onChange={e => setNewDept(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddDept())}
              />
              <button style={styles.secondaryBtn} onClick={handleAddDept}>Add</button>
              <button style={styles.primaryBtn} onClick={handleSaveDepts} disabled={savingDepts}>
                {savingDepts ? 'Saving...' : 'Save Departments'}
              </button>
            </div>
          )}
          {deptMsg && (
            <p style={{ ...styles.msg, color: deptMsg.includes('saved') ? '#16a34a' : '#dc2626' }}>
              {deptMsg}{' '}
              {/upgrade|plan allows/i.test(deptMsg) && <a href="/workspace?upgrade=1" style={{ color: '#fb923c', fontWeight: 700 }}>Upgrade →</a>}
            </p>
          )}
        </div>

        {/* ── How it works ── */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>⚡ How AI Department Classification Works</h2>
          <div style={styles.steps}>
            {[
              { n: '1', text: 'You create a new project with a title and description' },
              { n: '2', text: 'GoProjectFlow sends the project details to Claude AI' },
              { n: '3', text: 'Claude analyzes the content and picks the best-fit department' },
              { n: '4', text: 'The department is automatically assigned — you can always override it' },
            ].map(s => (
              <div key={s.n} style={styles.step}>
                <div style={styles.stepNum}>{s.n}</div>
                <p style={styles.stepText}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Orphaned Tasks Cleanup ── */}
        {['gpf_administrator','gpf_system_admin','administrator'].includes(JSON.parse(localStorage.getItem('gpf_user')||'{}').role) && orphanedTasks.length > 0 && (
          <div style={{ ...styles.card, border: '1.5px solid #fde68a', background: '#fffbeb' }}>
            <h2 style={{ ...styles.cardTitle, color: '#92400e' }}>🔗 Orphaned Tasks Found</h2>
            <p style={styles.cardDesc}>
              These {orphanedTasks.length} task{orphanedTasks.length !== 1 ? 's' : ''} no longer belong to any project — most likely left behind from a project deleted before automatic cleanup was added. Every task should belong to a project; you can review and remove these below.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16, maxHeight: 220, overflowY: 'auto' }}>
              {orphanedTasks.map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'white', borderRadius: 8, border: '1px solid #fde68a', fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{t.title}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Created {new Date(t.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
            <button
              style={{ padding: '10px 20px', background: '#d97706', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
              onClick={handleCleanupOrphans}
              disabled={cleaningOrphans}
            >
              {cleaningOrphans ? 'Cleaning up...' : `Delete ${orphanedTasks.length} Orphaned Task${orphanedTasks.length !== 1 ? 's' : ''}`}
            </button>
            {cleanupMsg && <p style={{ fontSize: 13, color: '#16a34a', marginTop: 10 }}>{cleanupMsg}</p>}
          </div>
        )}

        {/* ── Danger Zone: Delete Account ── */}
        {!isPlatformAdmin && ['gpf_administrator','gpf_system_admin'].includes(JSON.parse(localStorage.getItem('gpf_user')||'{}').role) && (
          <div style={{ ...styles.card, border: '1.5px solid #fecaca', background: '#fffafa' }}>
            <h2 style={{ ...styles.cardTitle, color: '#dc2626' }}>⚠️ Danger Zone</h2>
            <p style={styles.cardDesc}>
              Permanently delete this workspace — all projects, tasks, notes, documents, contacts, and every team member's access. This cannot be undone.
            </p>
            <button
              style={{ padding: '10px 20px', background: 'white', color: '#dc2626', border: '1.5px solid #dc2626', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
              onClick={() => setShowDeleteAccount(true)}
            >
              Delete My Account
            </button>
          </div>
        )}

      </div>

      {/* Delete Account Modal */}
      {showDeleteAccount && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 480, padding: 32, boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
            {deleteStep === 1 && (
              <>
                <h2 style={{ fontSize: 19, fontWeight: 800, margin: '0 0 6px' }}>We're sorry to see you go</h2>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 18px' }}>Before you go, could you tell us why? This helps us improve.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {DELETE_REASONS.map(r => (
                    <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: `1.5px solid ${deleteReason === r ? '#2563eb' : '#e2e8f0'}`, borderRadius: 8, cursor: 'pointer', background: deleteReason === r ? '#eff6ff' : 'white' }}>
                      <input type="radio" name="delete-reason" checked={deleteReason === r} onChange={() => setDeleteReason(r)} />
                      <span style={{ fontSize: 13, color: '#374151' }}>{r}</span>
                    </label>
                  ))}
                </div>
                <textarea
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 18 }}
                  placeholder="Anything else you'd like us to know? (optional)"
                  value={deleteNote}
                  onChange={e => setDeleteNote(e.target.value)}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button style={{ padding: '10px 18px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 14, cursor: 'pointer' }} onClick={() => setShowDeleteAccount(false)}>Cancel</button>
                  <button
                    style={{ padding: '10px 18px', border: 'none', borderRadius: 8, background: deleteReason ? '#dc2626' : '#fca5a5', color: 'white', fontSize: 14, fontWeight: 700, cursor: deleteReason ? 'pointer' : 'not-allowed' }}
                    disabled={!deleteReason}
                    onClick={() => setDeleteStep(2)}
                  >Continue →</button>
                </div>
              </>
            )}

            {deleteStep === 2 && (
              <>
                <h2 style={{ fontSize: 19, fontWeight: 800, color: '#dc2626', margin: '0 0 6px' }}>This is permanent</h2>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px', lineHeight: 1.7 }}>
                  Every project, task, note, document, contact, and team member on this workspace will be permanently deleted. There is no way to undo this.
                </p>
                <label style={styles.smallLabel}>Type DELETE to confirm</label>
                <input
                  style={{ ...styles.input, width: '100%', marginBottom: 16 }}
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoFocus
                />
                {deleteError && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{deleteError}</p>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button style={{ padding: '10px 18px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 14, cursor: 'pointer' }} onClick={() => setDeleteStep(1)}>← Back</button>
                  <button
                    style={{ padding: '10px 18px', border: 'none', borderRadius: 8, background: confirmText === 'DELETE' ? '#dc2626' : '#fca5a5', color: 'white', fontSize: 14, fontWeight: 700, cursor: confirmText === 'DELETE' ? 'pointer' : 'not-allowed' }}
                    disabled={confirmText !== 'DELETE' || deletingAccount}
                    onClick={handleDeleteAccount}
                  >{deletingAccount ? 'Deleting...' : 'Permanently Delete Account'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

const styles = {
  page:          { maxWidth: 760 },
  header:        { marginBottom: 28 },
  title:         { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle:      { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  card:          { background: '#fff', borderRadius: 10, padding: 28, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 20 },
  cardHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  cardTitle:     { fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' },
  cardDesc:      { fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.6 },
  connectedBadge:{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 99, fontSize: 12, fontWeight: 600, color: '#16a34a' },
  connectedDot:  { width: 8, height: 8, borderRadius: '50%', background: '#16a34a' },
  currentKey:    { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 12px', background: '#f8fafc', borderRadius: 6 },
  currentKeyLabel:{ fontSize: 12, color: '#64748b' },
  smallLabel:     { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 },
  webhookBox:     { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 14px', marginBottom: 16 },
  copyBtn:        { padding: '9px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  fieldWarning:   { fontSize: 12, color: '#d97706', margin: '5px 0 0', lineHeight: 1.5 },
  upgradeNote:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400e', flexWrap: 'wrap', gap: 8 },
  upgradeNoteLink:{ fontSize: 13, fontWeight: 700, color: '#fb923c', textDecoration: 'none' },
  settingsUpgradeBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #fff7ed, #fef3c7)', border: '1.5px solid #fed7aa', borderRadius: 12, padding: '16px 20px', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  settingsUpgradeBtn: { padding: '10px 22px', background: '#fb923c', color: 'white', borderRadius: 8, fontSize: 14, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' },
  currentKeyValue:{ fontSize: 13, fontWeight: 600, color: '#0f172a', fontFamily: 'monospace' },
  keyForm:       { display: 'flex', gap: 10, marginBottom: 12 },
  input:         { padding: '9px 12px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  primaryBtn:    { padding: '9px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  secondaryBtn:  { padding: '9px 16px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  msg:           { fontSize: 13, margin: '8px 0 0', fontWeight: 500 },
  infoBox:       { background: '#eff6ff', borderRadius: 7, padding: '10px 14px', marginTop: 12 },
  infoText:      { fontSize: 12, color: '#1e40af', margin: 0, lineHeight: 1.6 },
  link:          { color: '#2563eb', fontWeight: 600 },
  deptGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, margin: '16px 0' },
  deptChip:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 7, border: '1px solid #e2e8f0' },
  deptName:      { fontSize: 13, fontWeight: 600, color: '#0f172a' },
  deptRemove:    { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12, padding: '0 2px' },
  addDeptRow:    { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  steps:         { display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 },
  step:          { display: 'flex', alignItems: 'flex-start', gap: 14 },
  stepNum:       { width: 28, height: 28, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 },
  stepText:      { fontSize: 14, color: '#374151', margin: 0, lineHeight: 1.6, paddingTop: 3 },
};